/**
 * Drops and moves, decided in one place and as pure functions: the board's
 * drag handler, its keyboard "Move…" menu and the tests all ask these.
 */
import type { CardText, DropResolution, KanbanCardBase, TransitionMap } from './types';

/**
 * What dropping `card` (placed by the server in `from`) on `to` means.
 *   - same column, or nowhere: nothing
 *   - an act the card may take: open it
 *   - an act it may not take now: refused, with the act's own reason
 *   - a refusal: refused, with its reason and its "instead"
 *   - a pair the map does not list: refused with the map's default reason
 */
export function resolveDrop<C extends KanbanCardBase, Ctx>(
  map: TransitionMap<C, Ctx>, card: C, from: string, to: string | null | undefined, ctx: Ctx,
): DropResolution {
  if (!to || to === from) return { kind: 'none' };
  const t = map.moves[from] ? map.moves[from][to] : undefined;
  if (!t) return { kind: 'refuse', from, to, reason: map.fallbackReason(from, to, card) };
  if (t.kind === 'refuse') {
    return { kind: 'refuse', from, to, reason: typeof t.reason === 'function' ? t.reason(card, ctx) : t.reason, ...(t.instead ? { instead: t.instead } : {}) };
  }
  const ok = t.available(card, ctx);
  if (ok !== true) return { kind: 'refuse', from, to, reason: ok };
  return { kind: 'act', act: cardText(t.act, card, ctx), from, to, opens: cardText(t.opens, card, ctx), label: cardText(t.label, card, ctx) };
}

/** An ActTransition's act / label / opens, for this card: the string as given, or the function's answer. */
export function cardText<C, Ctx>(v: CardText<C, Ctx>, card: C, ctx: Ctx): string {
  return typeof v === 'function' ? v(card, ctx) : v;
}

/** A move on offer. `inPlace` marks an act that leaves the card in its column (TransitionMap.inPlace); `to` is then its own column. */
export interface OfferedMove { to: string; act: string; label: string; opens: string; inPlace?: boolean }

/**
 * The moves this card is OFFERED, in column order: only acts it may take now.
 * A refusal is never a menu item -- the menu is the non-drag way to do what a
 * drop would do, not a list of things that will not happen.
 */
export function offeredMoves<C extends KanbanCardBase, Ctx>(
  map: TransitionMap<C, Ctx>, card: C, ctx: Ctx, columnOrder: string[],
): OfferedMove[] {
  const from = card.column;
  const out: OfferedMove[] = [];
  for (const to of columnOrder) {
    if (to === from) continue;
    const t = map.moves[from] ? map.moves[from][to] : undefined;
    if (t && t.kind === 'act' && t.inMenu === false) continue; // the same act is offered by another column's item
    const r = resolveDrop(map, card, from, to, ctx);
    if (r.kind === 'act') {
      out.push({ to, act: r.act, label: r.label || r.act, opens: r.opens });
    }
  }
  if (map.inPlace) {
    for (const a of map.inPlace(card, ctx)) {
      if (a.available === true) out.push({ to: from, act: a.act, label: a.label, opens: a.opens || a.label, inPlace: true });
    }
  }
  return out;
}
